<script lang="ts">
import { defineBlock } from '@bagelink/blox'
import { defineSchema, $, Alert } from '@bagelink/vue'
const schema = defineSchema({
	useDatastoreData: $.checkbox('Choose content from datastore', { default: true }),
	datastoreExplanation: $.display(Alert, {
		info: true,
		thin: true,
		title: 'Choose a series or symposium',
		message:
			'Select the exact series or symposium to show. Disable “Choose content from datastore” to enter custom content manually.',
	}).if('useDatastoreData eq true'),
	seriesId: $.select('Series or Symposium', bigBoxOptions, {
		placeholder: bigBoxOptions.length
			? 'Choose a series or symposium'
			: 'No series or symposia found',
		searchable: true,
	}).if('useDatastoreData eq true'),
	tag: $.text('Tag').if('useDatastoreData eq false'),
	title: $.text('Title').if('useDatastoreData eq false'),
	subtitle: $.textarea('Subtitle', { rows: 3 }).if('useDatastoreData eq false'),
	image: $.upload('Image').if('useDatastoreData eq false'),
	link: $.text('Link').if('useDatastoreData eq false'),
})
export const BlockBigBox = defineBlock({
	thumbnail: '/block-thumbs/BlockBigBox.svg',
	category: 'Hero',
	order: 170,
	name: 'BlockBigBox',
	label: 'Big Box',
	icon: 'crop_landscape',
	schema,
	defaults: {
		useDatastoreData: true,
		seriesId: '',
		tag: 'Series',
		title: '',
		subtitle: '',
		image: '',
		link: '',
	},
	dataSource: [
		{ collection: 'series', store: 'the_torah', label: 'Series' },
		{ collection: 'symposia', store: 'the_torah', label: 'Symposia' },
	],
})
</script>
<script setup lang="ts">
import { useBloxRuntime } from '@bagelink/blox'
import { Image } from '@bagelink/vue'
import { computed } from 'vue'
import { resolveAuthorForBlock, useCollection, ensureAuthors } from '@/composables/useDatastore'
import { bigBoxOptions } from '@/components/blocks/bigBoxEditorOptions'
import { articleTitleText, sanitizeArticleTitleHtml } from '@/utils/articleHtml'



const props = defineProps<{
	useDatastoreData?: boolean
	seriesId?: string
	tag?: string
	title?: string
	subtitle?: string
	image?: string
	link?: string
}>()

ensureAuthors()
const PREVIEW_SERIES = {
	tag: 'A Series',
	title: 'A Featured Series',
	subtitle: 'Explore a collection of essays from leading scholars.',
	image: '/og.jpg',
	link: '/',
	isSymposium: false,
}

const { isPreview } = useBloxRuntime()
// Featured cards do not consume article bodies or enriched relationship graphs.
const cardFields = [
	'id',
	'_id',
	'slug',
	'title',
	'name',
	'subtitle',
	'description',
	'authorId',
	'image',
]
const { items: series, loading } = useCollection('series', {
	limit: 100,
	all: true,
	pick: cardFields,
})
const { items: symposia, loading: symposiaLoading } = useCollection('symposia', {
	limit: 100,
	all: true,
	pick: cardFields,
})
const content = computed(() => {
	if (props.useDatastoreData === false) {
		return {
			tag: props.tag || '',
			title: props.title || '',
			subtitle: props.subtitle || '',
			image: props.image || '',
			link: props.link || '#',
			isSymposium: false,
		}
	}

	const [selectedType, selectedId] = props.seriesId?.includes(':')
		? props.seriesId.split(':', 2)
		: ['series', props.seriesId]
	const collection = selectedType === 'symposia' ? symposia.value : series.value
	const item = (
		selectedId
			? collection.find(
					(entry: any) =>
						entry._id === selectedId || entry.id === selectedId || entry.slug === selectedId,
				)
			: undefined
	) as any | undefined
	if (!item && !loading.value && !symposiaLoading.value && isPreview.value) return PREVIEW_SERIES
	const author = item ? resolveAuthorForBlock(item.authorId) : undefined
	const isSymposium = selectedType === 'symposia'
	return {
		tag: item ? (isSymposium ? 'A Symposium' : 'A Series') : '',
		title: item?.title || item?.name || '',
		subtitle:
			item?.subtitle || item?.description || (author ? `By ${author.title} ${author.name}` : ''),
		image: item?.image || '',
		link: item?.slug ? `/${isSymposium ? 'symposium' : 'series'}/${item.slug}` : '#',
		isSymposium,
	}
})
</script>

<template>
	<div class="pt-1 pb-3 m_pt-1 m_pb-2 m_px-1">
		<router-link :to="content.link" class="decoration-none w850px block bg-gray txt-center">
			<Image
				v-if="content.image"
				:src="content.image"
				:alt="articleTitleText(content.title)"
				ratio="16-9"
				class="cover aspect-ratio-16-9 w-100p h-350px m_h-auto"
			/>
			<div class="p-2 w700px">
				<div v-if="content.tag" class="flex justify-content-center gap-1">
					<div class="dashline m_none w-100px" />
					<p class="txt17 color-primary">
						{{ content.tag }}
					</p>
					<div class="dashline m_none w-100px" />
				</div>
				<h3
					v-if="content.title"
					class="txt40 m_txt24 m-0 semibold line-height-14 pt-1-5 pb-2 pretty"
					v-html="sanitizeArticleTitleHtml(content.title)"
				/>
				<p v-if="content.subtitle" class="txt17" :class="{ 'pb-2': !content.isSymposium }">
					{{ content.subtitle }}
				</p>
				<div class="dashline" />
				<p v-if="content.isSymposium" class="txt17 color-primary pt-1-5">See all Responses</p>
			</div>
		</router-link>
	</div>
</template>
